import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Building2, 
  Plus,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  MapPin,
  Calendar,
  ArrowRight,
  MessageSquare,
  Phone,
  Mail
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientStillastjenester() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [newRequest, setNewRequest] = useState({
    title: "",
    description: "",
    location_address: "",
    location_city: "",
    service_type: "full_service",
    property_type: "residential",
    estimated_height_meters: "",
    estimated_area_sqm: "",
    desired_start_date: "",
    estimated_duration_weeks: "",
    special_requirements: "",
    contact_name: user?.name || "",
    contact_phone: user?.phone || "",
    contact_email: user?.email || "",
    is_private_client: true
  });

  useEffect(() => {
    fetchRequests();
  }, [token]);

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/stillastjenester/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setRequests(await res.json());
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!newRequest.title || !newRequest.description || !newRequest.location_address || !newRequest.location_city) {
      toast.error("Vennligst fyll ut alle obligatoriske felt");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/stillastjenester/request-quote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newRequest,
          estimated_height_meters: parseFloat(newRequest.estimated_height_meters) || null,
          estimated_area_sqm: parseFloat(newRequest.estimated_area_sqm) || null,
          estimated_duration_weeks: parseInt(newRequest.estimated_duration_weeks) || null
        })
      });

      if (!res.ok) throw new Error("Kunne ikke sende forespørsel");

      toast.success("Forespørsel sendt! Vi kontakter deg snart med tilbud.");
      setShowNewRequestModal(false);
      fetchRequests();
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuoteResponse = async (action, message = "") => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/stillastjenester/${selectedRequest.quote_request_id}/respond`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ action, message })
      });

      if (!res.ok) throw new Error("Kunne ikke svare på tilbudet");

      const data = await res.json();
      toast.success(data.message);
      setShowQuoteModal(false);
      fetchRequests();
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending_quote: { label: "Venter på tilbud", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
      quote_sent: { label: "Tilbud mottatt", color: "bg-blue-500/20 text-blue-400", icon: FileText },
      negotiating: { label: "Forhandling", color: "bg-orange-500/20 text-orange-400", icon: MessageSquare },
      quote_accepted: { label: "Akseptert", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
      project_live: { label: "Prosjekt aktivt", color: "bg-cyan-500/20 text-cyan-400", icon: Building2 },
      rejected: { label: "Avslått", color: "bg-red-500/20 text-red-400", icon: XCircle }
    };
    return configs[status] || { label: status, color: "bg-gray-500/20 text-gray-400", icon: Clock };
  };

  const getMinDate = () => {
    const today = new Date();
    // Allow selecting today's date
    return today.toISOString().split("T")[0];
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="client-stillastjenester">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Stillastjenester</h1>
            <p className="text-gray-400 mt-1">
              Be om tilbud på komplette stillasløsninger
            </p>
          </div>
          <Button 
            onClick={() => setShowNewRequestModal(true)}
            className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Be om tilbud
          </Button>
        </div>

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-[#23F7DD]/10 to-purple-500/10 border-[#23F7DD]/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#23F7DD]/20 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#23F7DD]" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Komplett stillasservice</h3>
                <p className="text-gray-400 text-sm">
                  AllStillas Holding leverer komplette stillasløsninger inkludert montering, 
                  utleie av utstyr, og demontering. Vi håndterer alt fra start til slutt.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge className="bg-[#23F7DD]/20 text-[#23F7DD]">Montering</Badge>
                  <Badge className="bg-[#23F7DD]/20 text-[#23F7DD]">Utstyrsleie</Badge>
                  <Badge className="bg-[#23F7DD]/20 text-[#23F7DD]">Demontering</Badge>
                  <Badge className="bg-[#23F7DD]/20 text-[#23F7DD]">HMS-godkjent</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-400 mt-4">Laster forespørsler...</p>
          </div>
        ) : requests.length === 0 ? (
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <h3 className="text-white font-semibold mb-2">Ingen forespørsler ennå</h3>
              <p className="text-gray-400 mb-6">
                Be om tilbud på stillastjenester for å komme i gang
              </p>
              <Button 
                onClick={() => setShowNewRequestModal(true)}
                className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
              >
                <Plus className="w-4 h-4 mr-2" />
                Be om tilbud
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => {
              const statusConfig = getStatusConfig(request.status);
              const StatusIcon = statusConfig.icon;
              const latestQuote = request.quotes?.[request.quotes.length - 1];
              
              return (
                <Card 
                  key={request.quote_request_id} 
                  className="bg-[#1a1a2e] border-[#2a2a4a] hover:border-[#23F7DD]/50 transition-colors cursor-pointer"
                  onClick={() => {
                    if (latestQuote && request.status === "quote_sent") {
                      setSelectedRequest(request);
                      setShowQuoteModal(true);
                    }
                  }}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg ${statusConfig.color} flex items-center justify-center flex-shrink-0`}>
                            <StatusIcon className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-white font-semibold">{request.title}</h3>
                              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                            </div>
                            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                <span>{request.location_city}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span>Ønsket start: {formatDate(request.desired_start_date)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {latestQuote && (
                          <div className="text-right">
                            <p className="text-[#23F7DD] font-bold text-xl">
                              {formatCurrency(latestQuote.total_with_vat)}
                            </p>
                            <p className="text-gray-500 text-sm">inkl. mva</p>
                          </div>
                        )}
                        {request.status === "quote_sent" && (
                          <Button className="bg-green-500 hover:bg-green-600 text-white">
                            Se tilbud
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* New Request Modal */}
        <Dialog open={showNewRequestModal} onOpenChange={setShowNewRequestModal}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#23F7DD]" />
                Be om tilbud på stillastjenester
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-gray-300">Prosjekttittel *</Label>
                  <Input
                    value={newRequest.title}
                    onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
                    placeholder="F.eks. Fasadestillas Enebolig"
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Type tjeneste *</Label>
                  <Select 
                    value={newRequest.service_type} 
                    onValueChange={(v) => setNewRequest({ ...newRequest, service_type: v })}
                  >
                    <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                      <SelectItem value="full_service">Full service (montering + leie + demontering)</SelectItem>
                      <SelectItem value="assembly">Kun montering</SelectItem>
                      <SelectItem value="disassembly">Kun demontering</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-gray-300">Type eiendom *</Label>
                  <Select 
                    value={newRequest.property_type} 
                    onValueChange={(v) => setNewRequest({ ...newRequest, property_type: v })}
                  >
                    <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                      <SelectItem value="residential">Bolig</SelectItem>
                      <SelectItem value="commercial">Næring</SelectItem>
                      <SelectItem value="industrial">Industri</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="col-span-2">
                  <Label className="text-gray-300">Beskrivelse *</Label>
                  <Textarea
                    value={newRequest.description}
                    onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                    placeholder="Beskriv prosjektet..."
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1 min-h-[100px]"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Adresse *</Label>
                  <Input
                    value={newRequest.location_address}
                    onChange={(e) => setNewRequest({ ...newRequest, location_address: e.target.value })}
                    placeholder="Gateadresse"
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">By *</Label>
                  <Input
                    value={newRequest.location_city}
                    onChange={(e) => setNewRequest({ ...newRequest, location_city: e.target.value })}
                    placeholder="Oslo, Bergen..."
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Estimert høyde (meter)</Label>
                  <Input
                    type="number"
                    value={newRequest.estimated_height_meters}
                    onChange={(e) => setNewRequest({ ...newRequest, estimated_height_meters: e.target.value })}
                    placeholder="Ca. meter"
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Estimert areal (m²)</Label>
                  <Input
                    type="number"
                    value={newRequest.estimated_area_sqm}
                    onChange={(e) => setNewRequest({ ...newRequest, estimated_area_sqm: e.target.value })}
                    placeholder="Ca. kvadratmeter"
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Ønsket startdato</Label>
                  <Input
                    type="date"
                    min={getMinDate()}
                    value={newRequest.desired_start_date}
                    onChange={(e) => setNewRequest({ ...newRequest, desired_start_date: e.target.value })}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Estimert varighet (uker)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={newRequest.estimated_duration_weeks}
                    onChange={(e) => setNewRequest({ ...newRequest, estimated_duration_weeks: e.target.value })}
                    placeholder="Antall uker"
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className="text-gray-300">Spesielle krav</Label>
                  <Textarea
                    value={newRequest.special_requirements}
                    onChange={(e) => setNewRequest({ ...newRequest, special_requirements: e.target.value })}
                    placeholder="Tilgang, parkering, HMS-krav..."
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div className="col-span-2 border-t border-[#2a2a4a] pt-4 mt-2">
                  <h4 className="text-white font-medium mb-3">Kontaktinformasjon</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-300">Navn *</Label>
                      <Input
                        value={newRequest.contact_name}
                        onChange={(e) => setNewRequest({ ...newRequest, contact_name: e.target.value })}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Telefon *</Label>
                      <Input
                        value={newRequest.contact_phone}
                        onChange={(e) => setNewRequest({ ...newRequest, contact_phone: e.target.value })}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">E-post *</Label>
                      <Input
                        value={newRequest.contact_email}
                        onChange={(e) => setNewRequest({ ...newRequest, contact_email: e.target.value })}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowNewRequestModal(false)} className="text-gray-400">
                Avbryt
              </Button>
              <Button 
                onClick={handleSubmitRequest}
                disabled={submitting}
                className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
              >
                {submitting ? "Sender..." : "Send forespørsel"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quote Detail Modal */}
        <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#23F7DD]" />
                Tilbud fra AllStillas Holding
              </DialogTitle>
            </DialogHeader>
            
            {selectedRequest && selectedRequest.quotes?.length > 0 && (
              <div className="space-y-6 py-4">
                {(() => {
                  const quote = selectedRequest.quotes[selectedRequest.quotes.length - 1];
                  return (
                    <>
                      <div className="bg-[#0f0f1a] rounded-xl p-6">
                        <h4 className="text-gray-400 text-sm mb-4">Prisdetaljer</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Montering</span>
                            <span className="text-white">{formatCurrency(quote.assembly_cost)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">
                              Leie ({quote.estimated_weeks} uker × {formatCurrency(quote.rental_cost_per_week)})
                            </span>
                            <span className="text-white">{formatCurrency(quote.rental_total)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Demontering</span>
                            <span className="text-white">{formatCurrency(quote.disassembly_cost)}</span>
                          </div>
                          {quote.equipment_cost > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-400">Utstyr</span>
                              <span className="text-white">{formatCurrency(quote.equipment_cost)}</span>
                            </div>
                          )}
                          {quote.other_costs > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-400">Andre kostnader</span>
                              <span className="text-white">{formatCurrency(quote.other_costs)}</span>
                            </div>
                          )}
                          <div className="border-t border-[#2a2a4a] pt-3 mt-3">
                            <div className="flex justify-between">
                              <span className="text-gray-400">Subtotal</span>
                              <span className="text-white">{formatCurrency(quote.subtotal)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">MVA (25%)</span>
                              <span className="text-white">{formatCurrency(quote.vat_amount)}</span>
                            </div>
                          </div>
                          <div className="border-t border-[#2a2a4a] pt-3">
                            <div className="flex justify-between items-center">
                              <span className="text-white font-semibold text-lg">Total</span>
                              <span className="text-[#23F7DD] font-bold text-2xl">
                                {formatCurrency(quote.total_with_vat)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {quote.description && (
                        <div className="bg-[#0f0f1a] rounded-xl p-4">
                          <h4 className="text-gray-400 text-sm mb-2">Beskrivelse</h4>
                          <p className="text-white">{quote.description}</p>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>Gyldig til: {formatDate(quote.valid_until)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          <span>Betaling: {quote.payment_terms === "50_50" ? "50% start, 50% slutt" : "Ved ferdigstillelse"}</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button 
                variant="ghost" 
                onClick={() => handleQuoteResponse("reject")}
                disabled={submitting}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Avslå
              </Button>
              <Button 
                variant="outline"
                onClick={() => handleQuoteResponse("negotiate", "Jeg ønsker å forhandle på prisen")}
                disabled={submitting}
                className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Forhandle
              </Button>
              <Button 
                onClick={() => handleQuoteResponse("accept")}
                disabled={submitting}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Aksepter tilbud
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
